import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone } from "lucide-react";
import { openSettings as openCookieSettings } from "@/utils/cookieConsent";

/**
 * Shared public site footer.
 *
 * Rendered by NexusLayout on every public route (/, /hemsidor,
 * /ai-chatbot-hemsida, /gratis-analys, /blog, …). Only link to routes
 * that actually exist in App.js so we never ship dead links.
 */

// Verified public contact info (matches website_content.personal + the
// Google Business Profile listed at share.google/9RTbLJTbmmTSv7A83).
const OFFICE_EMAIL = "hej@elido.nu";
const DAVID_PHONE = "072-370 81 58";

// Every href here is validated against /app/frontend/src/App.js routes.
// Hash targets resolve on the homepage (`/#anchor`) so they work from
// any subpage.
const NAV = {
  services: {
    title: "Tjänster",
    links: [
      { label: "Hemsidor", href: "/hemsidor" },
      { label: "Google Ads", href: "/google-ads-byra" },
      { label: "Meta Ads", href: "/meta-ads-byra" },
      { label: "SEO", href: "/seo-byra" },
      { label: "AI-chatbotar", href: "/ai-chatbot-hemsida" },
      { label: "System & automation", href: "/systemutveckling-foretag" },
    ],
  },
  explore: {
    title: "Utforska",
    links: [
      { label: "Kundcase", href: "/hemsidor#kundcase" },
      { label: "Priser", href: "/hemsidor#priser" },
      { label: "Om oss", href: "/om-oss" },
      { label: "Blogg", href: "/blog" },
      { label: "Kontakt", href: "/#kontakt" },
    ],
  },
};

// Verified public social profiles.
const SOCIAL = [
  {
    label: "Elido på Instagram",
    href: "https://www.instagram.com/elido.nu",
    Icon: Instagram,
    testId: "footer-social-instagram",
  },
  {
    label: "Elido på Facebook",
    href: "https://www.facebook.com/profile.php?id=61590757554392",
    Icon: Facebook,
    testId: "footer-social-facebook",
  },
];

function FooterLink({ href, children }) {
  const isInternal = href.startsWith("/") && !href.includes("#");
  if (isInternal) {
    return (
      <Link
        to={href}
        className="text-sm text-white/55 hover:text-white transition-colors"
      >
        {children}
      </Link>
    );
  }
  // Anchor + hash routes → let the browser do a full nav so /#kontakt
  // works even from /hemsidor.
  return (
    <a
      href={href}
      className="text-sm text-white/55 hover:text-white transition-colors"
    >
      {children}
    </a>
  );
}

function FooterColumn({ title, links, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/40 mb-5">
        {title}
      </div>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            <FooterLink href={l.href}>{l.label}</FooterLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function NexusFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-[#06090c] border-t border-white/5"
      data-testid="nexus-footer"
    >
      {/* Upper grid: 4 columns on desktop, stacked on mobile */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-12 lg:pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand block */}
          <div className="lg:col-span-5" data-testid="footer-brand">
            <Link to="/" className="inline-flex items-center mb-5" aria-label="Elido – till startsidan">
              <img
                src="/img/elido-logo-light.png"
                alt="Elido"
                width="1000"
                height="509"
                loading="lazy"
                decoding="async"
                className="h-9 w-auto"
                data-testid="footer-logo"
              />
            </Link>
            <p className="text-sm text-white/55 leading-relaxed max-w-sm">
              Vi hjälper företag med hemsidor, Google Ads, Meta Ads, SEO och
              AI-chatbotar. Från första klick till ny kund.
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/12 text-white/60 hover:text-white hover:border-white/35 hover:bg-white/5 transition-colors"
                  data-testid={s.testId}
                >
                  <s.Icon className="w-[18px] h-[18px]" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <FooterColumn
              title={NAV.services.title}
              links={NAV.services.links}
              testId="footer-services"
            />
          </div>
          <div className="lg:col-span-2">
            <FooterColumn
              title={NAV.explore.title}
              links={NAV.explore.links}
              testId="footer-explore"
            />
          </div>

          <div className="lg:col-span-3" data-testid="footer-contact">
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/40 mb-5">
              Kontakt
            </div>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${OFFICE_EMAIL}`}
                  className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#5DBCCB] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  {OFFICE_EMAIL}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${DAVID_PHONE.replace(/\s+/g, "")}`}
                  className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#5DBCCB] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  {DAVID_PHONE}
                </a>
              </li>
              <li className="pt-1">
                <a
                  href="/#kontakt"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/[0.03] text-xs font-semibold text-white hover:bg-white/[0.08] transition-colors"
                  data-testid="footer-contact-cta"
                >
                  Berätta vad ni vill förbättra
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs text-white/35" data-testid="footer-copyright">
            © {year} Elido AB · Org.nr 559553-0873
          </span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              to="/integritetspolicy"
              className="text-xs text-white/45 hover:text-white transition-colors"
              data-testid="footer-privacy"
            >
              Integritetspolicy
            </Link>
            <button
              type="button"
              onClick={openCookieSettings}
              className="text-xs text-white/45 hover:text-white transition-colors bg-transparent border-0 p-0 cursor-pointer"
              data-testid="footer-cookie-settings"
            >
              Cookieinställningar
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
